"use client";

import React, { useState, useEffect } from "react";
import type { CouponWithStoreData } from "@/types/couponsWithStoresData";
import { BadgeCheck } from "lucide-react";
import CouponModal from "@/components/coupon_popup";
import Image from "next/image";
import renderOembed from "@/lib/iframeFunction";
import { openAffiliateLink } from "@/lib/openAffiliateLink";
import { getStoreLogoAlt } from "@/lib/storeLogo";
import { AdRectangle2 } from "@/components/ads/GptAd";

interface TopDealsProps {
  deals: CouponWithStoreData[];
  couponId: string;
}

const TopDeals: React.FC<TopDealsProps> = ({ deals, couponId }) => {
  const [selectedDeal, setSelectedDeal] = useState<CouponWithStoreData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logoBackgrounds, setLogoBackgrounds] = useState<Record<string, string>>({});

  const getDiscountPercent = (discount?: string) => {
    const match = discount?.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? Number(match[1]) : 0;
  };
  const verifiedChoice = deals
    .filter((deal) => deal.verified && getDiscountPercent(deal.discount) > 0)
    .reduce<CouponWithStoreData | null>(
      (bestDeal, deal) =>
        !bestDeal || getDiscountPercent(deal.discount) > getDiscountPercent(bestDeal.discount)
          ? deal
          : bestDeal,
      null
    );

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (couponId) {
      const coupon = deals.find((c) => c._id === couponId);
      if (coupon) {
        setSelectedDeal(coupon);
        setIsModalOpen(true);
      }
    }
  }, [couponId, deals]);

  if (!deals.length) {
    return (
      <div className="max-w-5xl mx-auto py-3 sm:py-5 px-4 text-center text-purple-700 font-semibold">
        No deals available.
      </div>
    );
  }

  const handleGetDealClick = (deal: CouponWithStoreData) => {
    setSelectedDeal(deal);
    setIsModalOpen(true);

    if (deal.couponUrl) {
      openAffiliateLink(deal.couponUrl, { couponId: deal._id });
    } else if (deal.store?.slug && typeof window !== "undefined") {
      const storeSlug = deal.store.slug;
      window.setTimeout(() => {
        window.location.assign(`/stores/${storeSlug}`);
      }, 1000);
      return;
    }

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("couponId", deal._id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-3 sm:py-5 px-4 sm:px-6 lg:px-8">
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Top Deals</h2>
        <a href="/coupons?couponType=deal" aria-label="View all top deals" className="shrink-0 text-xs font-semibold uppercase text-purple-700 underline underline-offset-4 hover:text-purple-900">
          View All
        </a>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {deals.map((deal, index) => (
          <React.Fragment key={deal._id || index}>
          <article className="flex min-h-[230px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div
              className="relative flex h-24 items-center justify-center p-3"
              style={{ backgroundColor: logoBackgrounds[deal._id] || "#f3f4f6" }}
            >
              {deal._id === verifiedChoice?._id && (
                <span className="absolute left-2 top-2 z-10 inline-flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-sm bg-purple-800 px-2 py-1 text-[9px] font-semibold leading-tight text-white">
                  <BadgeCheck aria-hidden="true" className="h-3 w-3 shrink-0" />
                  <span>Verified Choice</span>
                </span>
              )}
              {deal.store?.image ? (
                <Image
                  src={`https://www.itscoupons.com${deal.store.image}`}
                  alt={getStoreLogoAlt(deal.store?.slug || deal.store?.name || deal.title)}
                  width={180}
                  height={96}
                  sizes="(max-width: 640px) 44vw, 180px"
                  loading="lazy"
                  className="h-full w-full object-contain"
                  onLoad={(event) => {
                    try {
                      const image = event.currentTarget;
                      const canvas = document.createElement("canvas");
                      canvas.width = 4;
                      canvas.height = 1;
                      const context = canvas.getContext("2d");
                      if (!context) return;

                      context.fillStyle = "#ffffff";
                      context.fillRect(0, 0, canvas.width, canvas.height);
                      const corners = [
                        [0, 0],
                        [image.naturalWidth - 1, 0],
                        [0, image.naturalHeight - 1],
                        [image.naturalWidth - 1, image.naturalHeight - 1],
                      ];
                      corners.forEach(([x, y], sampleIndex) => {
                        context.drawImage(image, x, y, 1, 1, sampleIndex, 0, 1, 1);
                      });

                      const pixels = context.getImageData(0, 0, 4, 1).data;
                      const color = [0, 1, 2].map((channel) => {
                        const samples = [0, 1, 2, 3]
                          .map((sampleIndex) => pixels[sampleIndex * 4 + channel])
                          .sort((a, b) => a - b);
                        return Math.round((samples[1] + samples[2]) / 2);
                      });
                      setLogoBackgrounds((current) => ({
                        ...current,
                        [deal._id]: `rgb(${color.join(", ")})`,
                      }));
                    } catch {
                      // Keep the neutral background if the image cannot be sampled.
                    }
                  }}
                />
              ) : (
                <span className="text-3xl font-bold text-purple-700">{deal.store?.name?.charAt(0) || "D"}</span>
              )}
              <span className="absolute left-2 bottom-2 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-800">
                {deal.discount || "Deal"} Off
              </span>
            </div>
            <div className="flex flex-1 flex-col p-3">
              <p className="mb-1 truncate text-[10px] font-bold uppercase text-gray-700">{deal.store?.name || "Featured Deal"}</p>
              <h3 className="line-clamp-2 min-h-10 text-sm font-semibold leading-5 text-gray-900">{deal.title}</h3>
              <p className="mt-1 line-clamp-2 text-xs leading-4 text-gray-600">{deal.description?.replace(/<[^>]*>/g, " ").trim() || "Save with this verified deal."}</p>
              {deal.couponCode && (
                <button
                  onClick={() => handleGetDealClick(deal)}
                  aria-label={`Get deal for ${deal.title}`}
                  className="mt-auto inline-flex h-9 w-full shrink-0 items-center justify-center rounded-full bg-purple-700 px-3 text-xs font-semibold leading-none text-white transition hover:bg-purple-800"
                >
                  Get Deal
                </button>
              )}
            </div>
          </article>
          {(index + 1) % 5 === 0 && index < deals.length - 1 && (
            <div className="col-span-full flex justify-center py-2">
              <AdRectangle2 />
            </div>
          )}
          </React.Fragment>
        ))}
      </div>

      <CouponModal
        storeName={selectedDeal?.store?.name}
        storeSlug={selectedDeal?.store?.slug}
        storeImageUrl={selectedDeal?.store?.image}
        title={selectedDeal?.title}
        discount={selectedDeal?.discount}
        code={selectedDeal?.couponCode}
        redeemUrl={selectedDeal?.couponUrl}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

const DescriptionWithToggle: React.FC<{ text?: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;
  const words = text.split(" ");
  const shouldTruncate = words.length > 6;
  const displayedText = expanded ? text : words.slice(0, 6).join(" ") + (shouldTruncate ? "..." : "");

  return (
    <div>
      <div className="text-xs text-gray-700 mt-1" dangerouslySetInnerHTML={{ __html: renderOembed(displayedText) }} />
      {shouldTruncate && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 text-xs m-1 font-medium hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

export default TopDeals;
